import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EVENTS as E } from '../../core/constants';
import { useSendEvents } from '../../core/hooks';
import { selectUserLocation } from '../../core/store/signup';
import { setAppLanguage, useCurrentLanguageItem } from '../../locales';
import {
  flagUrl,
  getAllLanguageItems,
  getLanguageSwitchUrl,
  getSuggestedLanguageItems,
  type LanguageItem,
} from '../../locales/languageConfig';
import { CloseIcon } from '../icons';
import * as S from './styled';

interface LanguageModalProps {
  readonly onClose: () => void;
  readonly screenId: string;
}

export const LanguageModal = ({ onClose, screenId }: LanguageModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const countryByIp = useSelector(selectUserLocation);
  const sendEvents = useSendEvents({ screenId });
  const current = useCurrentLanguageItem();
  const [loadingLocale, setLoadingLocale] = useState<string | null>(null);
  const loadEventSentRef = useRef(false);

  useEffect(() => {
    if (loadEventSentRef.current) return;
    sendEvents('language_modal__screen__load', { [E.SL]: 'language_modal' });
    loadEventSentRef.current = true;
  }, [sendEvents]);

  useEffect(() => {
    const { body, documentElement } = document;
    const prevBody = body.style.overflow;
    const prevHtml = documentElement.style.overflow;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      body.style.overflow = prevBody;
      documentElement.style.overflow = prevHtml;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const suggested = getSuggestedLanguageItems(navigator.language, countryByIp);
  const all = getAllLanguageItems(suggested);

  const handlePick = async (item: LanguageItem) => {
    if (loadingLocale) return;
    setLoadingLocale(item.locale);
    try {
      await setAppLanguage(item.locale);
      const { pathname, search, hash } = window.location;
      const nextUrl = getLanguageSwitchUrl(pathname, search, hash, item.locale);
      if (nextUrl) {
        navigate(nextUrl, { replace: true });
      }
      if (item !== current) {
        sendEvents('language_modal_language_picker__click', { [E.OS]: item.locale });
      }
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingLocale(null);
    }
  };

  const renderItem = (item: LanguageItem) => (
    <S.LangButton
      key={item.locale}
      type="button"
      $active={item === current}
      aria-current={item === current || undefined}
      disabled={!!loadingLocale}
      onClick={() => handlePick(item)}
    >
      <S.Flag src={flagUrl(item.flagFile)} alt="" />
      {item.label}
    </S.LangButton>
  );

  return createPortal(
    <S.Overlay
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <S.Panel role="dialog" aria-modal="true" aria-labelledby="language-modal-title">
        <S.HeadRow>
          <S.Title id="language-modal-title">{t('select_your_language')}</S.Title>
          <S.CloseBtn type="button" onClick={onClose} aria-label="Close" autoFocus>
            <CloseIcon />
          </S.CloseBtn>
        </S.HeadRow>
        <S.Scroll>
          <S.Section>
            <S.SectionTitle>{t('suggested_for_you')}</S.SectionTitle>
            {suggested.map(renderItem)}
          </S.Section>
          <S.Section>
            <S.SectionTitle>{t('all_languages')}</S.SectionTitle>
            {all.map(renderItem)}
          </S.Section>
        </S.Scroll>
      </S.Panel>
    </S.Overlay>,
    document.body,
  );
};
